import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faRefresh, 
  faQrcode, 
  faEllipsisVertical, 
  faCopy, 
  faCheck,
  faTrash,
  faSearch,
  faTimes,
  faPuzzlePiece
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { getInstanceStatus, deleteInstanceFull, restartInstance, updateInstancePhoneNumber, updateInstanceName } from '../../../services/api';
import ConnectQrModal from '../../../components/ConnectionTable/ConnectQrModal';
import IntegrationsModal from '../../../components/ConnectionTable/NovaConexao/Qrcode/SuccessQR/Integrations';
import { apiRequest } from '../../../config/api';
import './EnterpriseInstanceTable.css';

const EnterpriseInstanceTable = ({ 
  instances, 
  allInstances,
  instanceStatuses: externalInstanceStatuses,
  statusLoading: externalStatusLoading,
  // Paginação
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  onInstancesUpdate, 
  onCreateInstance, 
  onDeleteInstance: externalDeleteInstance,
  onRecreateInstance: externalRecreateInstance,
  currentUser,
  enterprise 
}) => {
  const { t } = useTranslation();
  


  const [copied, setCopied] = useState({});
  const [instanceStatuses, setInstanceStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectInstance, setConnectInstance] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartingInstanceId, setRestartingInstanceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInstances, setFilteredInstances] = useState([]);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [selectedInstanceForIntegrations, setSelectedInstanceForIntegrations] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [updatingName, setUpdatingName] = useState(false);

  // Usar status externos se fornecidos, senão usar internos
  const currentInstanceStatuses = externalInstanceStatuses || instanceStatuses;
  const currentStatusLoading = externalStatusLoading !== undefined ? externalStatusLoading : statusLoading;

  // Filtrar instâncias baseado no termo de pesquisa
  useEffect(() => {
    if (!instances) {
      setFilteredInstances([]);
      return;
    }

    const filtered = instances.filter(instance => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (instance.name && instance.name.toLowerCase().includes(searchLower)) ||
        (instance.instanceName && instance.instanceName.toLowerCase().includes(searchLower)) ||
        (instance.apikey && instance.apikey.toLowerCase().includes(searchLower)) ||
        (instance.phone_number && instance.phone_number.toLowerCase().includes(searchLower)) ||
        (instance.Users && instance.Users.name && instance.Users.name.toLowerCase().includes(searchLower)) ||
        (instance.Users && instance.Users.email && instance.Users.email.toLowerCase().includes(searchLower))
      );
    });

    setFilteredInstances(filtered);
  }, [instances, searchTerm]);

  // Usar instâncias filtradas para paginação
  const totalFilteredInstances = filteredInstances.length;
  const totalFilteredPages = Math.ceil(totalFilteredInstances / itemsPerPage);
  const startFilteredIndex = (currentPage - 1) * itemsPerPage;
  const endFilteredIndex = startFilteredIndex + itemsPerPage;
  const paginatedFilteredInstances = filteredInstances.slice(startFilteredIndex, endFilteredIndex);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Reset para primeira página quando pesquisar
    if (onPageChange) {
      onPageChange(1);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    // Reset para primeira página quando limpar
    if (onPageChange) {
      onPageChange(1);
    }
  };

  const handleOpenIntegrations = (instance) => {
    setSelectedInstanceForIntegrations(instance);
    setShowIntegrationsModal(true);
    setActionMenuOpen(null);
  };

  const handleCloseIntegrations = () => {
    setShowIntegrationsModal(false);
    setSelectedInstanceForIntegrations(null);
    // Recarrega as instâncias quando o modal é fechado
    if (onInstancesUpdate) {
      setTimeout(() => {
        onInstancesUpdate();
      }, 1000);
    }
  };

  const fetchAllInstanceStatuses = async () => {
    if (!instances.length || externalInstanceStatuses) return; // Não buscar se já vem de fora
    
    setStatusLoading(true);
    const statuses = {};
    let hasPhoneNumberUpdate = false;
    
    await Promise.all(
      instances.map(async (instance) => {
        try {
          const statusRes = await getInstanceStatus(
            instance.instanceName,
            instance.server_id
          );
          statuses[instance.id] = statusRes?.status || 'UNKNOWN';
          
          // Se a instância está conectada e não tem número de telefone, atualiza
          if (statusRes?.status === 'WORKING' && statusRes?.me?.id && !instance.phone_number) {
            // Extrai apenas o número do telefone, removendo @c.us
            const phoneNumber = statusRes.me.id.replace('@c.us', '');
            try {
              await updateInstancePhoneNumber(instance.id, phoneNumber);
              hasPhoneNumberUpdate = true;
            } catch (phoneError) {
              console.error('❌ Erro ao atualizar número:', phoneError);
            }
          }
        } catch {
          statuses[instance.id] = 'UNKNOWN';
        }
      })
    );
    
    setInstanceStatuses(statuses);
    setStatusLoading(false);
    
    // Se houve atualização de número de telefone, chama callback para recarregar
    if (hasPhoneNumberUpdate && onInstancesUpdate) {
      setTimeout(() => {
        onInstancesUpdate();
      }, 1000);
    }
  };

  useEffect(() => {
    if (!externalInstanceStatuses) {
      fetchAllInstanceStatuses();
    }
  }, [allInstances, externalInstanceStatuses]);

  // Fechar menu quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuOpen && !event.target.closest('.action-menu-container')) {
        setActionMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);

  const getStatusStyles = (instanceId) => {
    const realStatus = currentInstanceStatuses[instanceId];
    if (realStatus === 'WORKING') {
      return { color: '#00FF99', text: t('enterprise.instances.online') };
    }
    return { color: '#f59e0b', text: t('enterprise.instances.offline') };
  };

  const isInstanceConnected = (instance) => {
    const realStatus = currentInstanceStatuses[instance.id];
    return realStatus && String(realStatus).toUpperCase() === 'WORKING';
  };

  const truncate = (text, max = 24) => {
    if (!text) return '';
    return text.length > max ? text.substring(0, max) + '...' : text;
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
  };

  const handleOpenConnectModal = (instance) => {
    setConnectInstance({
      id: instance.id,
      name: instance.name || instance.instanceName,
      instance: instance.instanceName,
      instanceName: instance.instanceName,
      rawData: {
        server_id: instance.server_id
      }
    });
    setConnectModalOpen(true);
  };

  const handleCloseConnectModal = () => {
    setConnectModalOpen(false);
    setConnectInstance(null);
    // Recarrega as instâncias quando o modal é fechado
    if (onInstancesUpdate) {
      setTimeout(() => {
        onInstancesUpdate();
      }, 1000);
    }
  };

  const handleRestartInstance = async (instance) => {
    // Se tem função externa, usa ela (abre modal)
    if (externalRecreateInstance) {
      externalRecreateInstance(instance);
      setActionMenuOpen(null);
      return;
    }

    // Senão, executa direto (fallback)
    if (!window.confirm(`Tem certeza que deseja reiniciar a instância "${instance.name || instance.instanceName}"?`)) {
      return;
    }

    try {
      setRestartLoading(true);
      setRestartingInstanceId(instance.id);
      await restartInstance(instance.instanceName, instance.server_id);
      
      setTimeout(() => {
        fetchAllInstanceStatuses();
      }, 2000);
      
    } catch (error) {
      console.error('Erro ao reiniciar instância:', error);
      alert('Erro ao reiniciar instância: ' + error.message);
    } finally {
      setRestartLoading(false);
      setRestartingInstanceId(null);
      setActionMenuOpen(null);
    }
  };

  const handleDeleteInstance = async (instance) => {
    // Se tem função externa, usa ela (abre modal)
    if (externalDeleteInstance) {
      externalDeleteInstance(instance);
      setActionMenuOpen(null);
      return;
    }

    // Senão, executa direto (fallback)
    if (!window.confirm(`Tem certeza que deseja deletar a instância "${instance.name || instance.instanceName}"?`)) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteInstanceFull(instance, instance.instanceName, instance.server_id);

      await apiRequest(`/enterprises/${enterprise.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          current_instances: Math.max(0, (enterprise.current_instances || 0) - 1)
        })
      });

      onInstancesUpdate();
    } catch (error) {
      console.error('Erro ao deletar instância:', error);
      alert('Erro ao deletar instância: ' + error.message);
    } finally {
      setDeleteLoading(false);
      setActionMenuOpen(null);
    }
  };

  const toggleActionMenu = (instanceId) => {
    setActionMenuOpen(actionMenuOpen === instanceId ? null : instanceId);
  };

  const handleStartEditName = (instance) => {
    setEditingName(instance.id);
    setEditingNameValue(instance.name || instance.instanceName || '');
  };

  const handleCancelEditName = () => {
    setEditingName(null);
    setEditingNameValue('');
  };

  const handleSaveName = async (instance) => {
    if (!editingNameValue.trim()) {
      alert(t('enterprise.instances.nameRequired'));
      return;
    }

    try {
      setUpdatingName(true);
      
      await updateInstanceName(instance.id, editingNameValue.trim());
      
      // Chama callback para atualizar no componente pai
      if (onInstancesUpdate) {
        // Pequeno delay para garantir que o Supabase processe a atualização
        setTimeout(() => {
          onInstancesUpdate();
        }, 500);
      }
      
      setEditingName(null);
      setEditingNameValue('');
      
      // Mostra mensagem de sucesso
      alert(t('enterprise.instances.nameUpdated'));
      
    } catch (error) {
      console.error('❌ Erro ao atualizar nome:', error);
      alert(t('enterprise.instances.errorUpdatingName'));
    } finally {
      setUpdatingName(false);
    }
  };

  return (
    <div className="enterprise-instance-table">
      <div className="table-header">
        <h3>
          <FontAwesomeIcon icon={faQrcode} style={{ marginRight: '0.5rem' }} />
          {currentUser?.enterprise_role === 'Admin' ? t('enterprise.instances.title') : t('enterprise.instances.myInstances')}
        </h3>
        <div className="table-actions">
          <div className="pagination-info">
            <span>
              {searchTerm 
                ? t('enterprise.pagination.showing', { 
                    start: totalFilteredInstances > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0,
                    end: Math.min(currentPage * itemsPerPage, totalFilteredInstances),
                    total: totalFilteredInstances
                  })
                : t('enterprise.pagination.showing', { 
                    start: totalItems > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0,
                    end: Math.min(currentPage * itemsPerPage, totalItems),
                    total: totalItems
                  })
              }
            </span>
          </div>
          <button
            className="refresh-btn"
            onClick={() => {
              if (externalInstanceStatuses) {
                // Se status vem de fora, chama o callback do pai
                onInstancesUpdate && onInstancesUpdate();
              } else {
                // Senão, atualiza internamente
                fetchAllInstanceStatuses();
              }
            }}
            disabled={currentStatusLoading}
            title={t('enterprise.instances.refresh')}
          >
            <FontAwesomeIcon icon={faRefresh} spin={currentStatusLoading} />
          </button>
          {(currentUser?.enterprise_role === 'Admin' || currentUser?.enterprise_role === 'Normal') && (
            <button
              className="create-btn"
              onClick={onCreateInstance}
              title={t('enterprise.createInstance.title')}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t('enterprise.createInstance.title')}
            </button>
          )}
        </div>
      </div>

      {/* Barra de Pesquisa */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder={t('enterprise.instances.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="clear-search-btn"
              title={t('enterprise.instances.clearSearch')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-info">
            {t('enterprise.instances.searchResults', { count: totalFilteredInstances })}
          </div>
        )}
      </div>

      <div className="table-wrapper">
        <table className="instances-table">
                     <thead>
             <tr>
               <th>{t('enterprise.instances.name')}</th>
               <th>{t('enterprise.instances.host')}</th>
               <th>{t('enterprise.instances.instanceName')}</th>
               {currentUser?.enterprise_role === 'Admin' && <th>{t('enterprise.instances.owner')}</th>}
               <th>{t('enterprise.instances.apiKey')}</th>
               <th>{t('enterprise.instances.status')}</th>
               <th>{t('enterprise.instances.phone')}</th>
               <th>{t('enterprise.instances.actions')}</th>
             </tr>
           </thead>
          <tbody>
            {paginatedFilteredInstances.length === 0 ? (
              <tr>
                <td 
                  colSpan={currentUser?.enterprise_role === 'Admin' ? 8 : 7} 
                  style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}
                >
                  {searchTerm ? t('enterprise.instances.noSearchResults') : t('enterprise.instances.noInstances')}
                </td>
              </tr>
            ) : (
              paginatedFilteredInstances.map((instance) => {
                const statusStyles = getStatusStyles(instance.id);
                                 return (
                   <tr key={instance.id}>
                                           <td>
                        {editingName === instance.id ? (
                          <div className="edit-name-cell">
                            <input
                              type="text"
                              value={editingNameValue}
                              onChange={(e) => setEditingNameValue(e.target.value)}
                              className="edit-name-input"
                              maxLength={50}
                              disabled={updatingName}
                            />
                            <div className="edit-name-buttons">
                              <button
                                className="save-name-btn"
                                onClick={() => handleSaveName(instance)}
                                disabled={updatingName}
                                title={t('enterprise.instances.saveName')}
                              >
                                <FontAwesomeIcon icon={faCheck} />
                              </button>
                              <button
                                className="cancel-name-btn"
                                onClick={handleCancelEditName}
                                disabled={updatingName}
                                title={t('enterprise.instances.cancelEdit')}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="api-key-cell">
                            <span 
                              className="instance-name-text"
                              onClick={() => handleStartEditName(instance)}
                              title={t('enterprise.instances.editName')}
                            >
                              {truncate(instance.name || instance.instanceName, 10)}
                              {/* Debug: {instance.name ? `(name: ${instance.name})` : `(instanceName: ${instance.instanceName})`} */}
                            </span>
                            <button
                              className="copy-btn"
                              onClick={() => copyToClipboard(instance.name || instance.instanceName, `name-${instance.id}`)}
                            >
                              <FontAwesomeIcon
                                icon={copied[`name-${instance.id}`] ? faCheck : faCopy}
                                style={{ color: copied[`name-${instance.id}`] ? '#00FF99' : undefined }}
                              />
                            </button>
                          </div>
                        )}
                      </td>
                     <td>
                       <div className="api-key-cell">
                         <span>{truncate(instance.Servers?.host || 'Host não encontrado', 20)}</span>
                         <button
                           className="copy-btn"
                           onClick={() => copyToClipboard(instance.Servers?.host || 'Host não encontrado', `host-${instance.id}`)}
                         >
                           <FontAwesomeIcon
                             icon={copied[`host-${instance.id}`] ? faCheck : faCopy}
                             style={{ color: copied[`host-${instance.id}`] ? '#00FF99' : undefined }}
                           />
                         </button>
                       </div>
                     </td>
                     <td>{truncate(instance.instanceName)}</td>
                     {currentUser?.enterprise_role === 'Admin' && (
                       <td>
                         {instance.Users ? (
                           <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                             <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                               {truncate(instance.Users.name, 15)}
                             </span>
                             <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                               {instance.Users.enterprise_role || 'Normal'}
                             </span>
                           </div>
                         ) : (
                           <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                             {t('enterprise.instances.unknown')}
                           </span>
                         )}
                       </td>
                     )}
                     <td>
                       <div className="api-key-cell">
                         <span>{truncate(instance.apikey, 20)}</span>
                         <button
                           className="copy-btn"
                           onClick={() => copyToClipboard(instance.apikey, instance.id)}
                         >
                           <FontAwesomeIcon
                             icon={copied[instance.id] ? faCheck : faCopy}
                             style={{ color: copied[instance.id] ? '#00FF99' : undefined }}
                           />
                         </button>
                       </div>
                     </td>
                     <td>
                       <div className="status-cell">
                         <div
                           className="status-dot"
                           style={{ backgroundColor: statusStyles.color }}
                         />
                         {statusStyles.text}
                       </div>
                     </td>
                     <td>
                       <span className="phone-number">
                         {instance.phone_number || t('enterprise.instances.notConnected')}
                       </span>
                     </td>
                    <td>
                      <div className="actions-cell">
                        {!isInstanceConnected(instance) && (
                          <button
                            className="action-btn qr-btn"
                            onClick={() => handleOpenConnectModal(instance)}
                            title={t('enterprise.instances.connect')}
                          >
                            <FontAwesomeIcon icon={faQrcode} />
                          </button>
                        )}
                        <div className="action-menu-container">
                          <button
                            className="action-btn menu-btn"
                            onClick={() => toggleActionMenu(instance.id)}
                            title={t('enterprise.instances.moreOptions')}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </button>
                          {actionMenuOpen === instance.id && (
                            <div className="action-menu">
                              {isInstanceConnected(instance) && (
                                <button
                                  className="menu-item integrations"
                                  onClick={() => handleOpenIntegrations(instance)}
                                >
                                  <FontAwesomeIcon icon={faPuzzlePiece} />
                                  {t('enterprise.instances.integrations')}
                                </button>
                              )}
                              <button
                                className="menu-item restart"
                                onClick={() => handleRestartInstance(instance)}
                                disabled={restartLoading || restartingInstanceId === instance.id}
                              >
                                <FontAwesomeIcon 
                                  icon={faRefresh} 
                                  spin={restartingInstanceId === instance.id}
                                />
                                {restartingInstanceId === instance.id ? t('enterprise.instances.restarting') : t('enterprise.instances.restart')}
                              </button>
                              <button
                                className="menu-item delete"
                                onClick={() => handleDeleteInstance(instance)}
                                disabled={deleteLoading}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                {t('enterprise.instances.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação de instâncias */}
      {totalFilteredPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-controls">
            <select 
              value={itemsPerPage} 
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="items-per-page-select"
            >
              <option value={10}>10 {t('enterprise.pagination.perPage')}</option>
              <option value={25}>25 {t('enterprise.pagination.perPage')}</option>
              <option value={50}>50 {t('enterprise.pagination.perPage')}</option>
            </select>
            
            <div className="pagination-buttons">
              <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t('enterprise.pagination.previous')}
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: Math.min(5, totalFilteredPages) }, (_, i) => {
                  let pageNum;
                  if (totalFilteredPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalFilteredPages - 2) {
                    pageNum = totalFilteredPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => onPageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalFilteredPages}
              >
                {t('enterprise.pagination.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {connectInstance && (
        <ConnectQrModal
          isOpen={connectModalOpen}
          onClose={handleCloseConnectModal}
          connection={connectInstance}
          navigate={null}
          isEnterprise={true}
        />
      )}

      {showIntegrationsModal && selectedInstanceForIntegrations && (
        <IntegrationsModal
          onClose={handleCloseIntegrations}
          instanceName={selectedInstanceForIntegrations.instanceName}
          onWebhookInstance={() => {
            // Implementar se necessário
          }}
          connection={selectedInstanceForIntegrations}
        />
      )}
    </div>
  );
};

export default EnterpriseInstanceTable; 